import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import logo from '../assets/logo.svg';
import adminChar from '../assets/demo/admin-pi-char.png';
import teacherChar from '../assets/demo/teacher-pi-char.png';
import studentChar from '../assets/demo/student-pi-char.png';
import AppStatic, { isDemoBuild } from '../config/AppStatic';
import Card from './ui/Card';

const ROLE_CARDS = [
    {
        key: 'ADMIN',
        label: 'Admin',
        description: 'Manage schedules, teachers, and rooms.',
        image: adminChar,
        accent: 'hover:ring-indigo-300',
        highlight: 'from-indigo-50/60 to-white',
    },
    {
        key: 'TEACHERS',
        label: 'Teacher',
        description: 'See your classes and timetable with extra tools.',
        image: teacherChar,
        accent: 'hover:ring-emerald-300',
        highlight: 'from-emerald-50/60 to-white',
    },
    {
        key: 'STUDENTS',
        label: 'Student',
        description: 'View your weekly schedule at a glance.',
        image: studentChar,
        accent: 'hover:ring-blue-300',
        highlight: 'from-blue-50/60 to-white',
    }
];

function DemoOnboardingPage() {
    const navigate = useNavigate();

    if (!isDemoBuild()) {
        return <Navigate to="/schedule" replace />;
    }

    const chooseRole = (roleKey) => {
        AppStatic.setBuildRole(roleKey);
        navigate('/schedule', { replace: true });
    };

    return (
        <div className="min-h-screen bg-[#f7f8fb] flex flex-col items-center justify-center px-4 py-14 sm:py-16">

            {/* ── HERO SECTION ── */}
            <div className="w-full max-w-xl flex flex-col items-center text-center gap-5 mb-14">
                <div className="flex items-start justify-center mb-1">
                    <img src={logo} alt="PI-Schedule logo" className="w-11 h-11 shrink-0 drop-shadow-sm" />
                    <div className="flex flex-col items-start text-left">
                        <span className="text-lg font-semibold tracking-tight text-slate-800 leading-tight">
                            PI-Schedule
                        </span>
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-blue-500 leading-none">
                            Demo
                        </p>
                    </div>
                </div>

                <div>
                    <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight tracking-tight">
                        Welcome to our demo
                    </h1>
                </div>

                <p className="text-base text-slate-500 leading-relaxed max-w-sm">
                    Explore PI-Schedule with sample data and experience the different features and views — no setup required.
                </p>

            </div>

            {/* ── DIVIDER ── */}
            <div className="w-full max-w-3xl flex flex-col items-center gap-4 mb-10">
                <div className="w-full flex items-center gap-4">
                    <div className="flex-1 h-px bg-slate-200" />
                    <div className="flex items-center gap-2 shrink-0">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                        <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                            Choose your portal
                        </p>
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                    </div>
                    <div className="flex-1 h-px bg-slate-200" />
                </div>
                <div className="text-center space-y-2 max-w-md px-2">
                    <p className="text-sm text-slate-600 leading-snug">
                        Experience the app as one of the 3 roles.
                    </p>
                    <p className="text-sm font-medium text-blue-400 leading-relaxed">
                        You can switch roles at any time using the demo bar at the top of the page.
                    </p>
                </div>
            </div>

            {/* ── PORTAL CARDS ── */}
            <div className="w-full max-w-3xl">
                <div className="grid gap-4 sm:grid-cols-3">
                    {ROLE_CARDS.map((role) => (
                        <button
                            key={role.key}
                            type="button"
                            onClick={() => chooseRole(role.key)}
                            className={`group text-left rounded-2xl ring-1 ring-slate-200 ${role.accent} hover:ring-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 bg-white`}
                        >
                            {/* Card image area */}
                            <div className={`rounded-t-2xl bg-gradient-to-b ${role.highlight} px-6 pt-6 pb-3 flex justify-center`}>
                                <img
                                    src={role.image}
                                    alt=""
                                    className="w-28 h-28 object-contain drop-shadow group-hover:scale-[1.04] transition-transform duration-200"
                                />
                            </div>

                            {/* Card content */}
                            <div className="px-5 pb-5 pt-3 flex flex-col gap-3">
                                <div>
                                    <h2 className="text-xl font-bold text-slate-900 mb-1">{role.label}</h2>
                                    <p className="text-sm text-slate-500 leading-snug">{role.description}</p>
                                </div>

                                <span className="inline-flex items-center gap-1 text-sm font-semibold text-slate-800 group-hover:text-blue-600 transition-colors">
                                    Continue as {role.label}
                                    <svg className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M3 8h10M9 4l4 4-4 4" />
                                    </svg>
                                </span>
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default DemoOnboardingPage;